import { Injectable, NgModule } from '@angular/core';
import {
  TranslocoLoader,
  TranslocoModule,
  TranslocoService,
  provideTransloco,
} from '@jsverse/transloco';
import {
  TranslocoDecimalPipe,
  TranslocoLocaleModule,
  TranslocoLocaleService,
  provideTranslocoLocale,
} from '@jsverse/transloco-locale';
import { I18nService, Locale } from '@precisely/prism-ng/i18n';
import { PrimeNGConfig } from 'primeng/api';
import { of, throwError } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { map } from 'rxjs/operators';
import en from '../assets/i18n/en.json';

@Injectable({ providedIn: 'root' })
export class StaticLoader implements TranslocoLoader {
  getTranslation(lang: string) {
    switch (lang) {
      case 'en':
        return of(en);
      default:
        // Need to throw error here if we want transloco's fallback functionality to kick-in.
        return throwError(new Error(`Translation file ${lang}.json not found`));
    }
  }
}
@NgModule({
  imports: [TranslocoLocaleModule, TranslocoDecimalPipe],
  exports: [TranslocoModule, TranslocoLocaleModule],
  providers: [
    provideTransloco({
      config: {
        availableLangs: ['de', 'en', 'es', 'fr', 'hi', 'iw', 'ja', 'te'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        missingHandler: {
          logMissingKey: false,
          useFallbackTranslation: true,
        },
      },
      loader: StaticLoader,
    }),
    provideTranslocoLocale({}),
  ],
})
export class TranslocoRootModule {
  private readonly TEST_TRANSLATIONS = {
    dis: {
      test: {
        navItem1: 'Nav Item 1',
        navItem2: 'Nav Item 2',
      },
    },
  };

  constructor(
    private transloco: TranslocoService,
    private translocoLocale: TranslocoLocaleService,
    private i18n: I18nService,
    private primeConfig: PrimeNGConfig,
  ) {
    transloco.setTranslation(this.TEST_TRANSLATIONS, 'en', { merge: true });
    const locale = this.parseLocaleFromQueryParam('en-US');
    this.setLocale(locale);

    // Currently using polling to check for locale changes via the url query param.  If performance becomes an issue,
    // we can look to monkey patching history.pushState instead.  But this is probably sufficient for Storybook.
    setInterval(() => {
      const locale = this.parseLocaleFromQueryParam('en-US');
      if (locale !== this.i18n.locale.toString()) {
        this.setLocale(locale);
      }
    }, 300);
  }

  private setLocale(locale: string) {
    this.transloco.setActiveLang(locale.substring(0, 2));
    this.translocoLocale.setLocale(locale);
    this.i18n.locale = new Locale(locale);
    this.transloco
      .selectTranslateObject('primeng')
      .pipe(
        take(1),
        map((translations) => this.primeConfig.setTranslation(translations)),
      )
      .subscribe();
  }

  private parseLocaleFromQueryParam(fallback: string): string {
    let locale: string = fallback;
    const matches: Array<string> =
      window.location.search.match(/locale:([^&^;]*)/)!;
    if (matches) {
      locale = matches[1];
    }
    return locale;
  }
}
